import { Collapse, message } from "antd";
import { useState, useRef, useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import CustomerForm, { CustomerFormRef } from "./CustomerInfo/CustomerForm";
import CreateOrder, { CreateOrderRef } from "./CustomerInfo/CreateOrder";
import OrderList from "./CustomerInfo/OrderList";
import { ArrowLeft } from "lucide-react";
import CustomerDropdown from "./CustomerInfo/CustomerDropdown";
import useAuthStore from "../../store/authStore";
import CustomerImgList from "./CustomerInfo/CustomerImgList";
import {
    User,
    Images,
    ClipboardList,
    CalendarDays,
} from "lucide-react";

export default function CustomerInfo({ user, setShowInfo, onCustomerCreated }: any) {
    const { t } = useTranslation();
    const { isSuperAdmin, hasPermission } = useAuthStore();
    const canEditCustomer = isSuperAdmin || hasPermission("customer.edit");
    const canCreateAppointment = isSuperAdmin || hasPermission("appointment.create");
    const customerFormRef = useRef<CustomerFormRef>(null);
    const createOrderRef = useRef<CreateOrderRef>(null);
    const [isDirty, setIsDirty] = useState(false);
    const [isOrderDirty, setIsOrderDirty] = useState(false);
    const [updatingCustomer, setUpdatingCustomer] = useState(false);
    const [creatingAppointment, setCreatingAppointment] = useState(false);
    const [activeKeys, setActiveKeys] = useState<string[]>(["1"]);

    const handleDirtyChange = useCallback((dirty: boolean) => {
        setIsDirty(dirty);
    }, []);

    // Lỗi tạo lịch hẹn do thiếu địa chỉ -> mở panel Thông tin khách hàng để người dùng cập nhật ngay
    useEffect(() => {
        const onFocusAddress = () => setActiveKeys(["1"]);
        window.addEventListener("focus-customer-address", onFocusAddress);
        return () => window.removeEventListener("focus-customer-address", onFocusAddress);
    }, []);

    // Nút "Giới tính và Ngày sinh" trên header phòng chat: mở panel Thông tin khách hàng
    // (để form chắc chắn đã mount) rồi bật hộp thoại qua tín hiệu timestamp.
    const [genderBirthdaySignal, setGenderBirthdaySignal] = useState(0);
    useEffect(() => {
        const onOpenGenderBirthday = () => {
            setActiveKeys(["1"]);
            setGenderBirthdaySignal(Date.now());
        };
        window.addEventListener("open-gender-birthday", onOpenGenderBirthday);
        return () => window.removeEventListener("open-gender-birthday", onOpenGenderBirthday);
    }, []);

    const handleOrderDirtyChange = useCallback((dirty: boolean) => {
        setIsOrderDirty(dirty);
    }, []);

    const handleUpdateCustomer = async () => {
        // Open customer info panel and close others
        setActiveKeys(["1"]);
        // Save the form
        if (customerFormRef.current) {
            setUpdatingCustomer(true);
            try {
                await customerFormRef.current.save();
            } finally {
                setUpdatingCustomer(false);
            }
        }
    };

    const handleCreateAppointment = async () => {
        // Chưa tạo khách hàng (chưa có CRM customer) -> không cho tạo lịch hẹn
        if (user?.customer == null) {
            message.warning(t("roomchat.create_appointment_need_customer") || "Vui lòng tạo khách hàng trước khi tạo lịch hẹn");
            return;
        }
        // Form thông tin khách đang có thay đổi chưa lưu (vd vừa nhập địa chỉ)
        // -> lưu trước để lịch hẹn dùng dữ liệu mới nhất; lưu thất bại thì dừng
        if (isDirty && customerFormRef.current) {
            setUpdatingCustomer(true);
            try {
                const saved = await customerFormRef.current.save();
                if (!saved) return;
            } finally {
                setUpdatingCustomer(false);
            }
        }
        // Open "Dịch vụ quan tâm" panel and close others
        setActiveKeys(["4"]);
        // Submit the order form
        if (createOrderRef.current) {
            setCreatingAppointment(true);
            try {
                const success = await createOrderRef.current.submit();
                if (success) {
                    // Close "Dịch vụ quan tâm" and open "Danh sách lịch hẹn"
                    setActiveKeys(["5"]);
                }
            } finally {
                setCreatingAppointment(false);
            }
        }
    };

    const handleOrderSubmitSuccess = useCallback(() => {
        // Switch to appointment list panel
        setActiveKeys(["5"]);
    }, []);

    const items = [
        {
            key: "1",
            label: (
                <div className="flex items-center gap-2">
                    <User size={16} />
                    <span>{t("roomchat.customer_info")}</span>
                </div>
            ),
            children: <CustomerForm ref={customerFormRef} channelCustomerId={user?.id} customer={user?.customer ?? null} displayName={user?.name} onDirtyChange={handleDirtyChange} isReadOnly={!canEditCustomer} onCreated={onCustomerCreated} openGenderBirthdaySignal={genderBirthdaySignal} />,
        },
        {
            key: "2",
            label: (
                <div className="flex items-center gap-2">
                    <Images size={16} />
                    <span>{t("roomchat.customer_images")}</span>
                </div>
            ),
            children: <CustomerImgList customerId={user?.id} />,
        },
        ...(canCreateAppointment ? [{
            key: "4",
            label: (
                <div className="flex items-center gap-2">
                    <ClipboardList size={16} />
                    <span>{t("roomchat.interested_service")}</span>
                </div>
            ),
            children: <CreateOrder ref={createOrderRef} customerId={user?.id} onDirtyChange={handleOrderDirtyChange} onSubmitSuccess={handleOrderSubmitSuccess} />,
        }] : []),
        {
            key: "5",
            label: (
                <div className="flex items-center gap-2">
                    <CalendarDays size={16} />
                    <span>{t("roomchat.appointment_list")}</span>
                </div>
            ),
            children: <OrderList customerId={user?.id} />,
        },
    ];

    return (
        <div className="h-full overflow-y-auto pb-[124px] relative">
            <div className="xl:hidden bg-white dark:bg-[#1f1f1f] flex items-center gap-2 p-3 border-b dark:border-[#2a2a2a]  text-000000e0 sticky top-0 z-10">

                <button
                    onClick={() => setShowInfo(false)}
                    className="text-lg"
                >
                    <ArrowLeft size={20} />
                </button>

                <span className="font-medium">
                    {t("roomchat.go_back")}
                </span>

            </div>
            <Collapse
                items={items}
                bordered={false}
                activeKey={activeKeys}
                onChange={(keys) => setActiveKeys(keys as string[])}
                className="customer-collapse"
                expandIconPosition="end"
            />
            <CustomerDropdown isDirty={isDirty} isOrderDirty={isOrderDirty} onUpdateCustomer={handleUpdateCustomer} onCreateAppointment={handleCreateAppointment} customerId={user?.id} crmCustomerId={user?.customer?.id ?? null} updatingCustomer={updatingCustomer} creatingAppointment={creatingAppointment} canEditCustomer={canEditCustomer} canCreateAppointment={canCreateAppointment} isNewCustomer={user?.customer == null} />
        </div>
    );
}